import { requestApi, parseResponse } from '~/services/api-new';
import { createImageViewFactory, generateCampaignImageSrcSetFactory } from '~/components/utilities/image-format';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';
import { Configuration } from '~/configuration/configuration';
import {
	EnhancedCampaign,
	EnhancedCampaignImage,
	EnhancedCategory,
	EnhancedGame
} from '~/components/casino-shared/types';
import { State } from '../ducks/types';
import { LiveOnWebResponse } from '../types';

const host = '/';

const enhanceLiveData = async (payload: Configuration, state: State) => {
	const response = parseResponse<LiveOnWebResponse>(
		await requestApi({
			endpoint: `${host}${`rest/state/casino/explore/live-on-web`}`,
			additionalOptions: { cache: 'default' }
		})
	);

	if ('error' in response) {
		return response;
	}

	const gameNamesByIdx = state.gameNamesByIdx.slice();
	const gameCodesByProviderCode = { ...state.gameCodesByProviderCode };
	const gameCodesBySubProviderCode = { ...state.gameCodesBySubProviderCode };

	const gameCodesByPublishCode = { ...state.gameCodesByPublishCode };
	const gameCodesByIdx = state.gameCodesByIdx.slice();

	const providers = { ...state.providers };
	const subProviders = { ...state.subProviders };

	const {
		imageDescriptors,
		imageFormats,
		campaignBackgroundImageDescriptors,
		campaignForegroundImageDescriptors,
		license
	} = payload;

	const formatEnhancer = createImageViewFactory(imageFormats, imageDescriptors);

	const enhanceCampaignImage = generateCampaignImageSrcSetFactory(
		campaignBackgroundImageDescriptors || [],
		campaignForegroundImageDescriptors || []
	);

	const { campaigns, categories, games } = response;

	const enhancedGames = games.reduce<Record<string, EnhancedGame>>((acc, currGame, gameIndex) => {
		const game = { ...currGame, ...getGameUrls(currGame, license) };

		gameNamesByIdx[gameIndex] = game.name;
		gameCodesByIdx[gameIndex] = game.code;

		const publishCode = game.publishCode?.split('_').reverse()[0];

		if (publishCode) {
			gameCodesByPublishCode[publishCode] = game.code;
		}

		if (gameCodesByProviderCode[game.provider.code]) {
			gameCodesByProviderCode[game.provider.code].push(game.code);
		} else {
			gameCodesByProviderCode[game.provider.code] = [game.code];
		}

		if (game.casinoSubProviderVO) {
			if (gameCodesBySubProviderCode[game.casinoSubProviderVO.code]) {
				gameCodesBySubProviderCode[game.casinoSubProviderVO.code].push(game.code);
			} else {
				gameCodesBySubProviderCode[game.casinoSubProviderVO.code] = [game.code];
			}

			subProviders[game.casinoSubProviderVO.code] = game.casinoSubProviderVO;
		}

		providers[game.provider.code] = { ...game.provider };

		const enhancedGame: EnhancedGame = {
			...game,
			imageView: enhanceImageView(game.imageView, imageDescriptors, formatEnhancer, false)
		};

		acc[game.code] = enhancedGame;

		return acc;
	}, {});

	const enhancedCampaigns: EnhancedCampaign[] = campaigns.map(banner => ({
		...banner,
		backgroundImage: enhanceCampaignImage(banner.backgroundImage, 'background') as EnhancedCampaignImage,
		foregroundImage1: banner.foregroundImage1
			? enhanceCampaignImage(banner.foregroundImage1, 'foreground')
			: undefined,
		foregroundImage2: banner.foregroundImage2
			? enhanceCampaignImage(banner.foregroundImage2, 'foreground')
			: undefined,
		tcImage: banner.tcImage ? enhanceCampaignImage(banner.tcImage, 'foreground') : undefined
	}));

	const enhancedCategories: EnhancedCategory[] = categories.map(category => {
		if (category.type === 'winner_feed') {
			return {
				...category,
				...(category.gamesByIdx
					? {
							gameCodes: category.gamesByIdx.map(index => games[index].code)
						}
					: null),
				winners: category.winners.map(({ gameIdx, ...winnersRest }) => ({
					...winnersRest,
					gameCode: games[gameIdx].code
				}))
			};
		}

		return {
			...category,
			...(category.gamesByIdx
				? {
						gameCodes: category.gamesByIdx.map(index => games[index].code)
					}
				: null)
		};
	});

	return {
		games: enhancedGames,
		gameCodesByIdx,
		gameNamesByIdx,
		gameCodesByProviderCode,
		gameCodesBySubProviderCode,
		gameCodesByPublishCode,
		providers,
		subProviders,
		campaigns: enhancedCampaigns,
		exploreCategories: enhancedCategories
	};
};

export default enhanceLiveData;
